import React, { useState, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers";
import * as dayjs from "dayjs";
import IntlMessages from "util/IntlMessages";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid
} from "@material-ui/core";
import FormInput from "components/Form/Controls/input";
import FormSelectAutoComplete from "components/Form/Controls/select-autocomplete";
import FormDatePicker from "components/Form/Controls/datepicker";
import ButtonAsync from "components/Form/Controls/ButtonAsync";

const SylvantForm = ({
    rowObj,
    prescriptionId,
    openCRU,
    handleRequestClose,
    handleCreateUpdate,
    readOnly,
    DiagnosticCodeCIEList,
    boolList
}) => {
    const getCboValue = (items, id) => {
        if (typeof id === "object") return id;
        const result = items.find((x) => x.id === id);
        return result ? { label: result.name, value: result.id } : "";
    };

    const defaultValues = {
        DiagnosticCodeCIE: null,
        numberVialsApproved: "",
        numberVialsApplied: "",
        numberVialsPurchased: "",
        numberVialsNotApplied: "",
        vialsWillNotBeUsed: "",
        importNotAvailable: null,
        submissionDate: null,
        autoDate: null,
        approvalDate: null,
        authorizationNumber: "",
        awardedTo: "",
        startDateTreatment: null,
        other: ""
    };



    const validationSchema = yup.object().shape({
        DiagnosticCodeCIE: yup
            .object()
            .nullable(),
        numberVialsApproved: yup
            .number()
            .nullable()
            .transform((value, originalValue) => originalValue === '' ? null : value)
            .typeError(<IntlMessages id="yup.numericValidation" />),
        numberVialsApplied: yup
            .number()
            .nullable()
            .transform((value, originalValue) => originalValue === '' ? null : value)
            .typeError(<IntlMessages id="yup.numericValidation" />),
        numberVialsPurchased: yup
            .number()
            .nullable()
            .transform((value, originalValue) => originalValue === '' ? null : value)
            .typeError(<IntlMessages id="yup.numericValidation" />),
        numberVialsNotApplied: yup
            .number()
            .nullable()
            .transform((value, originalValue) => originalValue === '' ? null : value)
            .typeError(<IntlMessages id="yup.numericValidation" />),
        vialsWillNotBeUsed: yup
            .number()
            .nullable()
            .transform((value, originalValue) => originalValue === '' ? null : value)
            .typeError(<IntlMessages id="yup.numericValidation" />),
        importNotAvailable: yup.object().nullable(),
        submissionDate: yup.date().nullable(),
        autoDate: yup.date().nullable(),
        approvalDate: yup.date().nullable(),
        authorizationNumber: yup.string().matches(/^[0-9]*$/, "Solo números").max(50, "Máximo 50 caracteres"),
        awardedTo: yup.string().nullable(),
        startDateTreatment: yup.date().nullable(),
        other: yup.string().max(200, "Máximo 200 caracteres").when("DiagnosticCodeCIE", {
            is: (val) => val && val.value === 16,
            then: (schema) => schema.required(<IntlMessages id="core.fieldRequired" />),
            otherwise: (schema) => schema.nullable()
        })
    });

    const methods = useForm({
        defaultValues,
        resolver: yupResolver(validationSchema),
        mode: "onChange"
    });

    const { handleSubmit, setValue, errors, watch, trigger } = methods;
    
    const diagnosticCodeCIEValue = watch("DiagnosticCodeCIE");
    const isOtherSelected = diagnosticCodeCIEValue?.value === 16;

    const formatDateOrNull = (value) =>
        value && dayjs(value).isValid()
            ? dayjs(value).format("YYYY-MM-DDTHH:mm:ss")
            : null;

    useEffect(() => {
        if (openCRU) {
            setTimeout(() => loadData(rowObj), 150);
        }
    }, [openCRU]);

    useEffect(() => {
        if (diagnosticCodeCIEValue) {
            if (diagnosticCodeCIEValue.value !== 16) {
                setValue("other", "");
            }
            trigger("other");
        } else {
            setValue("other", "");
        }
    }, [diagnosticCodeCIEValue, trigger, setValue]);

    const loadData = (row) => {
        if (!row) return;

        setValue("DiagnosticCodeCIE", row.ciE10_11 ? getCboValue(DiagnosticCodeCIEList, row.ciE10_11) : null);
        setValue("numberVialsApproved", row.numberVialsApproved ?? "");
        setValue("numberVialsApplied", row.numberVialsApplied ?? "");
        setValue("numberVialsPurchased", row.numberVialsPurchased ?? "");
        setValue("numberVialsNotApplied", row.numberVialsNotApplied ?? "");
        setValue("vialsWillNotBeUsed", row.vialsWillNotBeUsed ?? "");
        setValue("importNotAvailable", row.importNotAvailable !== null ? { label: row.importNotAvailable ? "Sí" : "No", value: row.importNotAvailable ? 1 : 0 } : null);
        setValue("submissionDate", row.submissionDate ? dayjs(row.submissionDate) : null);
        setValue("autoDate", row.autoDate ? dayjs(row.autoDate) : null);
        setValue("approvalDate", row.approvalDate ? dayjs(row.approvalDate) : null);
        setValue("authorizationNumber", row.authorizationNumber ?? "");
        setValue("awardedTo", row.awardedTo ?? "");
        setValue("startDateTreatment", row.startDateTreatment ? dayjs(row.startDateTreatment) : null);
        setValue("other", row.other ?? "");
    };

    const onSubmit = async (d) => {
        const obj = {
            ...rowObj,
            id: rowObj?.id ?? 0,
            prescriptionId: prescriptionId,
            CIE10_11: d.DiagnosticCodeCIE?.value ?? null,
            numberVialsApproved: d.numberVialsApproved ? Number(d.numberVialsApproved) : null,
            numberVialsApplied: d.numberVialsApplied ? Number(d.numberVialsApplied) : null,
            numberVialsPurchased: d.numberVialsPurchased ? Number(d.numberVialsPurchased) : null,
            numberVialsNotApplied: d.numberVialsNotApplied ? Number(d.numberVialsNotApplied) : null,
            vialsWillNotBeUsed: d.vialsWillNotBeUsed ? Number(d.vialsWillNotBeUsed) : null,
            importNotAvailable: d.importNotAvailable === null ? null : d.importNotAvailable.value === 1,
            submissionDate: formatDateOrNull(d.submissionDate),
            autoDate: formatDateOrNull(d.autoDate),
            approvalDate: formatDateOrNull(d.approvalDate),
            authorizationNumber: d.authorizationNumber,
            awardedTo: d.awardedTo,
            startDateTreatment: formatDateOrNull(d.startDateTreatment),
            other: d.other || null
        };
        await handleCreateUpdate(obj);
    };

    const disabled = !!readOnly;

    return (
        <Dialog fullWidth maxWidth="md" open={openCRU} onClose={handleRequestClose}>
            <DialogTitle>
                <IntlMessages id={!rowObj ? "core.add" : "core.edit"} />
            </DialogTitle>
            <DialogContent>
                <FormProvider {...methods}>
                    <form noValidate autoComplete="off">
                        <Grid
                            container
                            spacing={2}
                            sx={{ '& .MuiFormControl-root': { width: '100%' }, '& .MuiInputBase-root': { height: 40 } }}
                        >
                            <Grid item xs={12} md={6}>
                                <FormSelectAutoComplete
                                    name="DiagnosticCodeCIE"
                                    label={<IntlMessages id="sylvant.cie10" />}
                                    options={DiagnosticCodeCIEList}
                                    disabled={disabled}
                                    errorobj={errors}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormSelectAutoComplete
                                    name="importNotAvailable"
                                    label={<IntlMessages id="vitalNotAvailable.importNotAvailable" />}
                                    options={boolList}
                                    disabled={disabled}
                                    errorobj={errors}
                                    margin="dense"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    name="other"
                                    label={<IntlMessages id="sylvant.other" />}
                                    disabled={disabled || !isOtherSelected}
                                    required={isOtherSelected}
                                    errorobj={errors}
                                    margin="dense"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    name="numberVialsApproved"
                                    label={<IntlMessages id="sylvant.numberVialsApproved" />}
                                    disabled={disabled}
                                    errorobj={errors}
                                    margin="dense"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    name="numberVialsApplied"
                                    label={<IntlMessages id="sylvant.numberVialsApplied" />}
                                    disabled={disabled}
                                    errorobj={errors}
                                    margin="dense"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    name="numberVialsPurchased"
                                    label={<IntlMessages id="sylvant.numberVialsPurchased" />}
                                    disabled={disabled}
                                    errorobj={errors}
                                    margin="dense"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    name="numberVialsNotApplied"
                                    label={<IntlMessages id="sylvant.numberVialsNotApplied" />}
                                    disabled={disabled}
                                    errorobj={errors}
                                    margin="dense"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    name="vialsWillNotBeUsed"
                                    label={<IntlMessages id="sylvant.vialsWillNotBeUsed" />}
                                    disabled={disabled}
                                    errorobj={errors}
                                    margin="dense"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormDatePicker
                                    name="submissionDate"
                                    label={<IntlMessages id="vitalNotAvailable.submissionDate" />}
                                    format="YYYY-MM-DD"
                                    disabled={disabled}
                                    errorobj={errors}
                                    margin="dense"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormDatePicker
                                    name="autoDate"
                                    label={<IntlMessages id="vitalNotAvailable.autoDate" />}
                                    format="YYYY-MM-DD"
                                    disabled={disabled}
                                    errorobj={errors}
                                    margin="dense"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormDatePicker
                                    name="approvalDate"
                                    label={<IntlMessages id="vitalNotAvailable.approvalDate" />}
                                    format="YYYY-MM-DD"
                                    disabled={disabled}
                                    errorobj={errors}
                                    margin="dense"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    name="authorizationNumber"
                                    label={<IntlMessages id="vitalNotAvailable.authorizationNumber" />}
                                    disabled={disabled}
                                    errorobj={errors}
                                    margin="dense"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    name="awardedTo"
                                    label={<IntlMessages id="vitalNotAvailable.awardedTo" />}
                                    disabled={disabled}
                                    errorobj={errors}
                                    margin="dense"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormDatePicker
                                    name="startDateTreatment"
                                    label={<IntlMessages id="vitalNotAvailable.startDateTreatment" />}
                                    format="YYYY-MM-DD"
                                    disabled={disabled}
                                    errorobj={errors}
                                    margin="dense"
                                />
                            </Grid>
                        </Grid>
                    </form>
                </FormProvider>
                {!disabled && (
                    <div className="text-center mt-4">
                        <ButtonAsync
                            onClick={handleSubmit(onSubmit)}
                            variant="contained"
                            color="primary"
                        >
                            <IntlMessages id="core.save" />
                        </ButtonAsync>
                    </div>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleRequestClose} color="secondary">
                    <IntlMessages id="core.close" />
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SylvantForm;
